import React, { useState, useEffect } from 'react';
import {
    Shield,
    Users,
    Building,
    UserCheck,
    User,
    CheckCircle,
    XCircle,
    AlertTriangle,
    TrendingUp,
    Activity,
    Calendar,
    RefreshCw,
    UserPlus,
    Settings,
    BarChart3,
    Database
} from 'lucide-react';
import UserManagement from './UserManagement';

const SuperAdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
        const interval = setInterval(fetchStats, 30000); // Refresh every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const fetchStats = async () => {
        try {
            const response = await fetch('https://api.zapbooksai.subraps.com/api/auth/super-admin-stats', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            const data = await response.json();
            if (data.success) {
                setStats(data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusStats = () => {
        if (!stats?.userAccessStats) return { active: 0, inactive: 0, suspended: 0 };

        return stats.userAccessStats.reduce((acc, stat) => {
            acc[stat.status] = (acc[stat.status] || 0) + stat.count;
            return acc;
        }, { active: 0, inactive: 0, suspended: 0 });
    };

    const getUserTypeStats = () => {
        if (!stats?.userAccessStats) return { approver: 0, employee: 0 };

        return stats.userAccessStats.reduce((acc, stat) => {
            acc[stat.user_type] = (acc[stat.user_type] || 0) + stat.count;
            return acc;
        }, { approver: 0, employee: 0 });
    };

    const statusStats = getStatusStats();
    const typeStats = getUserTypeStats();
    const totalUsers = stats?.userAccessStats.reduce((sum, stat) => sum + stat.count, 0) || 0;

    const tabs = [
        { id: 'overview', name: 'Overview', icon: BarChart3 },
        { id: 'users', name: 'User Management', icon: Users },
        { id: 'settings', name: 'System Settings', icon: Settings }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4">
                        <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Dashboard</h2>
                    <p className="text-gray-600">Fetching system statistics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold flex items-center gap-3">
                            <Shield className="w-8 h-8" />
                            Super Admin Dashboard
                        </h1>
                        <p className="text-purple-100 mt-2">Complete system administration and user management</p>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-purple-100">Last Updated</p>
                        <p className="text-lg font-medium">
                            {stats?.generatedAt ? new Date(stats.generatedAt).toLocaleTimeString() : 'Loading...'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => {
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                        ? 'border-purple-500 text-purple-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <TabIcon className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'
                                    }`} />
                                {tab.name}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Main Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Total Users */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <Users className="w-6 h-6 text-blue-600" />
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                                    <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
                                </div>
                            </div>
                        </div>

                        {/* Active Users */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                        <CheckCircle className="w-6 h-6 text-green-600" />
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Active Users</p>
                                    <p className="text-2xl font-bold text-gray-900">{statusStats.active}</p>
                                    <p className="text-sm text-green-600">
                                        {totalUsers > 0 ? `${((statusStats.active / totalUsers) * 100).toFixed(1)}%` : '0%'} of total
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Approvers */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <UserCheck className="w-6 h-6 text-purple-600" />
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Approvers</p>
                                    <p className="text-2xl font-bold text-gray-900">{typeStats.approver}</p>
                                    <p className="text-sm text-purple-600">
                                        {totalUsers > 0 ? `${((typeStats.approver / totalUsers) * 100).toFixed(1)}%` : '0%'} of total
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Companies */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                                        <Building className="w-6 h-6 text-teal-600" />
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Companies</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats?.topCompanies?.length || 0}</p>
                                    <p className="text-sm text-teal-600">Active organizations</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Status Breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* User Status Distribution */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                <Activity className="w-5 h-5 text-gray-600" />
                                User Status Distribution
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                        <span className="text-sm text-gray-600">Active</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{statusStats.active}</span>
                                        <div className="w-20 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-green-500 h-2 rounded-full"
                                                style={{ width: `${totalUsers > 0 ? (statusStats.active / totalUsers) * 100 : 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                                        <span className="text-sm text-gray-600">Inactive</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{statusStats.inactive}</span>
                                        <div className="w-20 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-gray-500 h-2 rounded-full"
                                                style={{ width: `${totalUsers > 0 ? (statusStats.inactive / totalUsers) * 100 : 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                        <span className="text-sm text-gray-600">Suspended</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{statusStats.suspended}</span>
                                        <div className="w-20 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-red-500 h-2 rounded-full"
                                                style={{ width: `${totalUsers > 0 ? (statusStats.suspended / totalUsers) * 100 : 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* User Type Distribution */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                <User className="w-5 h-5 text-gray-600" />
                                User Type Distribution
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <UserCheck className="w-4 h-4 text-purple-600" />
                                        <span className="text-sm text-gray-600">Approvers</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{typeStats.approver}</span>
                                        <div className="w-20 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-purple-500 h-2 rounded-full"
                                                style={{ width: `${totalUsers > 0 ? (typeStats.approver / totalUsers) * 100 : 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-green-600" />
                                        <span className="text-sm text-gray-600">Employees</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{typeStats.employee}</span>
                                        <div className="w-20 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-green-500 h-2 rounded-full"
                                                style={{ width: `${totalUsers > 0 ? (typeStats.employee / totalUsers) * 100 : 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Companies and Recent Users */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Top Companies */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                <Building className="w-5 h-5 text-gray-600" />
                                Top Companies
                            </h3>
                            <div className="space-y-3">
                                {stats?.topCompanies?.slice(0, 5).map((company, index) => (
                                    <div key={index} className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{company.company_name}</p>
                                            <p className="text-xs text-gray-500">{company.active_users} active users</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">{company.user_count}</span>
                                            <span className="text-xs text-gray-500">total</span>
                                        </div>
                                    </div>
                                )) || (
                                        <p className="text-sm text-gray-500">No company data available</p>
                                    )}
                            </div>
                        </div>

                        {/* Recent Users */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-gray-600" />
                                Recent Users
                            </h3>
                            <div className="space-y-3">
                                {stats?.recentUsers?.map((user, index) => (
                                    <div key={index} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                                <User className="w-4 h-4 text-gray-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                                <p className="text-xs text-gray-500">{user.company_name || 'No company'}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.user_type === 'approver'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : 'bg-green-100 text-green-800'
                                                }`}>
                                                {user.user_type}
                                            </span>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {new Date(user.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                )) || (
                                        <p className="text-sm text-gray-500">No recent users</p>
                                    )}
                            </div>
                        </div>
                    </div>

                    {/* System Health */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                            <Database className="w-5 h-5 text-gray-600" />
                            System Health
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm text-gray-600">Database Connected</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm text-gray-600">Authentication Service</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm text-gray-600">API Endpoints</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'users' && <UserManagement />}

            {activeTab === 'settings' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-gray-600" />
                        System Settings
                    </h3>
                    <p className="text-gray-600">System settings and configuration options will be available here.</p>
                </div>
            )}
        </div>
    );
};

export default SuperAdminDashboard;